// =============================================================================
// selectors/propReport.ts — the Prop Report, scope-enforced.
// -----------------------------------------------------------------------------
// ⚠ `ui/` may not import `domain/` at all, so everything the tab renders comes
// through here. That is the mechanical rule; this file also carries the reason
// it exists at all.
//
// ── 🚨 THIS IS NOT A PASS-THROUGH, AND THAT IS THE WHOLE POINT (D47) ──
//
// `selectors/patternVersions.ts` re-exports its domain module unchanged and says
// so honestly: those functions are Class W by construction, with no population to
// scope. These are the opposite. Combined DD remaining, every progress bar and
// every profit-target percentage is a function of the ENTIRE trade history, and a
// domain function is correct for whatever array it is handed — so calling one
// with a bounded read produces a drawdown buffer that reads LARGER than the room
// the account actually has, self-consistently, with nothing on screen wrong.
//
// So each wrapper takes `WholeBook<T>` rather than `readonly T[]`. Handing it a
// `Population<T>` is a compile error until the caller narrows, and the caller has
// to write the else-branch. The block is a type error, not a review comment.
//
// ⚠ `isWholeBook` is re-checked at runtime in `buildAggregate` and here for the
// same reason: a cast defeats a type, and the `as WholeBook<T>` cast is how the
// compile-time half will actually be defeated in practice.
// =============================================================================

import {
  dailyPnlHeatmap as dailyPnlHeatmapImpl,
  fundedStatus as fundedStatusImpl,
  progressBars as progressBarsImpl,
  type Heatmap,
  type FundedStatus,
  type MoneyFormats,
  type ProgressBar,
  type PropGroups,
} from '../domain/propReport';
import {
  propTiltSignals as propTiltSignalsImpl,
  type TiltComposite, type TiltTrade,
} from '../domain/propTilt';
import type { AccountTrade } from '../domain/account';
import type { DayBoundaryMode } from '../domain/range';
import { isWholeBook, type WholeBook } from './population';

export { describeSpan } from '../domain/propTilt';
export type { TiltComposite, TiltLevel, TiltReason, TiltTrade } from '../domain/propTilt';

export { groupPropAccounts, classifyPropAccount, PROP_FEE_TYPES, PROP_REVENUE_TYPES }
  from '../domain/propReport';
export type {
  FundedStatus, Heatmap, MoneyFormats, ProgressBar, PropAccount, PropBucket, PropGroups,
} from '../domain/propReport';

/**
 * The three account-rule engines the Day Overview's prop rails need.
 *
 * ⚠ RE-EXPORTED RATHER THAN REIMPLEMENTED, and that is the whole point.
 * `computeConsistencyMath` already carries a registered parity delta (D28 /
 * PD-1) against the old client's `propConsistency`, which contradicted itself:
 * with no profitable day it hardcoded total profit to 0, but with one green day
 * and a negative net it returned the real negative figure. A second
 * implementation on the Day Overview would either reintroduce that bug or
 * silently disagree with the Accounts tab about whether a payout gate is met.
 *
 * ⚠ These take the population as a PARAMETER — they do not read a store. The
 * caller supplies `tradesInAttempt`, because an account that was reset carries
 * days from a previous attempt the firm no longer counts.
 */
export {
  computeConsistencyMath, computeProfitTargetProgress, tradesInAttempt,
} from '../domain/account';
export type { ConsistencyMath, ProfitProgress } from '../domain/account';

/**
 * The guard every wrapper below runs.
 *
 * ⚠ Its message names the surface AND the direction of the error. "Population is
 * not a whole book" tells the next reader nothing they can act on; "this number
 * would read larger than the room that exists" tells them the safe response is to
 * block the screen rather than render what loaded.
 */
function requireWholeBook<T>(p: WholeBook<T>, what: string): readonly T[] {
  if (!isWholeBook(p)) {
    throw new Error(
      `${what}: the population is not a whole book. This value is a function of the entire ` +
        `trade history — computed from a partial read it is not less precise, it is wrong in ` +
        `the OPTIMISTIC direction and agrees with itself while wrong. Block the surface.`,
    );
  }
  return p.rows;
}

export function fundedStatus<T extends AccountTrade>(
  groups: PropGroups,
  population: WholeBook<T>,
  ytdStart: string,
  ytdEnd: string,
  today: string,
  mode: DayBoundaryMode,
): FundedStatus {
  const rows = requireWholeBook(population, 'fundedStatus');
  return fundedStatusImpl(groups, rows, ytdStart, ytdEnd, today, mode);
}

export function progressBars<T extends AccountTrade>(
  groups: PropGroups,
  population: WholeBook<T>,
  today: string,
  mode: DayBoundaryMode,
  fmt: MoneyFormats,
): ProgressBar[] {
  const rows = requireWholeBook(population, 'progressBars');
  return progressBarsImpl(groups, rows, today, mode, fmt);
}

/**
 * ⚠ The heatmap is the ONE card here that is genuinely Class W — thirty days of
 * daily sums, and a read covering those thirty days would answer it correctly.
 *
 * It still takes a `WholeBook`, and the reason is worth stating rather than
 * leaving as an inconsistency: it is drawn from the same read as the cards above
 * it, on the same screen, about the same accounts. Giving this one a looser
 * signature would let a future caller feed the heatmap a bounded read and the
 * bars a whole one, so two cards on one screen would describe two different
 * populations — which is exactly the disagreement this layer exists to prevent.
 */
export function dailyPnlHeatmap<T extends AccountTrade>(
  groups: PropGroups,
  population: WholeBook<T>,
  endToday: string,
  days = 30,
): Heatmap | null {
  const rows = requireWholeBook(population, 'dailyPnlHeatmap');
  return dailyPnlHeatmapImpl(groups, rows, endToday, days);
}

/**
 * The Tilt Signal banner.
 *
 * 🚨 THE GATE MATTERS MORE HERE THAN ANYWHERE ELSE ON THIS TAB, and in the
 * opposite direction from every other wrapper above. Those produce a NUMBER that
 * reads too large; this one produces SILENCE. Three of its five signals compare a
 * recent window against an all-time baseline, so a bounded read drags the
 * baseline up to meet the window and the ratio collapses to ~1 — the sizing
 * creep, the frequency spike and the grade decay all stop firing, and the loss
 * streak gets truncated at the read boundary. Nothing on screen looks wrong. The
 * banner simply is not there.
 *
 * A drawdown gauge that overstates your room is caught the moment you check the
 * firm's dashboard. A tilt warning that never appears is never caught at all.
 *
 * ⚠ It takes the account ids the CALLER resolved rather than a `PropGroups`, so
 * `domain/propTilt` never has to know what a prop account is — the same split
 * that keeps it importable into an Edge Function.
 */
export function propTiltSignals<T extends TiltTrade>(
  accountIds: ReadonlySet<string>,
  population: WholeBook<T>,
): TiltComposite {
  const rows = requireWholeBook(population, 'propTiltSignals');
  return propTiltSignalsImpl(accountIds, rows);
}

/** The accounts the tilt banner watches — funded + eval, i.e. what you can still blow. */
export function liveAccountIds(groups: PropGroups): ReadonlySet<string> {
  return new Set([...groups.funded, ...groups.eval].map((a) => a.id));
}

/** Accounts the report shows at all — `excludeFromMetrics` already dropped. */
export function propAccountCount(groups: PropGroups): number {
  return (Object.keys(groups) as (keyof PropGroups)[])
    .reduce((s, k) => s + groups[k].length, 0);
}
